import { Building2 } from 'lucide-react';
import { formatPhone } from '@/lib/utils';
import { PatientAvatar } from '@/components/patients/PatientAvatar';
import { CopyButton } from '@/components/ui';

interface ContactInfoHeaderProps {
  /** patientID or contactID — seeds the avatar gradient when no photo is present */
  id: number;
  name: string;
  photoUrl?: string | null;
  phone?: string | null;
  /** The phone's stored dial code ('44'); absent or blank formats as +1. */
  phoneCountryCode?: string | null;
  email?: string | null;
  /** Pre-joined address string, or null/undefined to omit */
  address?: string | null;
  /** ISO date (YYYY-MM-DD); rendered as "Month D, YYYY (age)" when present */
  birthDate?: string | null;
  /** Pre-resolved facility name; rendered under DOB, same as the Details tab */
  facilityName?: string | null;
  /** Long-form inbound comm-log address (acct{accountId}-0-0-{patientId}@{replyDomain}) */
  commLogEmail?: string | null;
  /** Additional label/value lines appended under DOB. Empty values are skipped. */
  extraFields?: Array<{ label: string; value: string }>;
}

/** Replace underscores/hyphens with spaces and title-case each word. */
function formatExtraLabel(label: string): string {
  return label
    .replace(/[_-]/g, ' ')
    .replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/** Clip very long values so they don't overwhelm the sidebar. */
function truncateValue(value: string, max = 80): string {
  return value.length > max ? value.slice(0, max) + '…' : value;
}

function formatDob(birthDate: string): string | null {
  if (!birthDate || birthDate.startsWith('0000')) return null;
  const dob = new Date(birthDate + 'T00:00:00');
  if (isNaN(dob.getTime())) return null;
  const age = Math.floor((Date.now() - dob.getTime()) / 31557600000);
  const dateStr = dob.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return `${dateStr} (${age})`;
}

export function ContactInfoHeader({
  id,
  name,
  photoUrl,
  phone,
  phoneCountryCode,
  email,
  address,
  birthDate,
  facilityName,
  commLogEmail,
  extraFields,
}: ContactInfoHeaderProps) {
  const trimmedName = name.trim();
  const dobStr = birthDate ? formatDob(birthDate) : null;
  const filteredExtras = (extraFields ?? []).filter(
    (f) => typeof f.value === 'string' && f.value.trim() !== '',
  );
  const trimmedCommLogEmail = commLogEmail?.trim() || '';
  const trimmedFacilityName = facilityName?.trim() || '';
  const hasAnyField = !!(
    trimmedName ||
    phone ||
    email ||
    address ||
    dobStr ||
    trimmedFacilityName ||
    trimmedCommLogEmail ||
    filteredExtras.length
  );

  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-[42%] flex items-start justify-center">
        <PatientAvatar
          patientId={id || 0}
          photoUrl={photoUrl}
          name={trimmedName || 'Contact'}
          size="3xl"
        />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        {trimmedName && (
          <p className="text-sm font-semibold text-slate-900">{trimmedName}</p>
        )}
        {phone && <p className="text-sm text-slate-700">{formatPhone(phone, phoneCountryCode ?? undefined)}</p>}
        {email && <p className="text-sm text-slate-700 truncate">{email}</p>}
        {address && <p className="text-sm text-slate-700 break-words">{address}</p>}
        {dobStr && <p className="text-sm text-slate-700">{dobStr}</p>}
        {trimmedFacilityName && (
          <p className="text-sm text-slate-700 flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span className="min-w-0 break-words">{trimmedFacilityName}</span>
          </p>
        )}
        {trimmedCommLogEmail && (
          <p className="text-xs text-slate-500 flex items-start gap-1.5">
            <span className="min-w-0 break-all">
              <span className="font-medium text-slate-600">Comm Log: </span>
              {trimmedCommLogEmail}
            </span>
            <CopyButton
              value={trimmedCommLogEmail}
              label="comm log email"
              className="mt-0.5 shrink-0"
            />
          </p>
        )}
        {filteredExtras.map((f, idx) => (
          <p
            key={`${f.label}-${idx}`}
            className="text-sm text-slate-700 break-words"
          >
            <span className="font-medium text-slate-800">{formatExtraLabel(f.label) || '—'}:</span>{' '}
            <span className="text-slate-700" title={f.value.length > 80 ? f.value : undefined}>
              {truncateValue(f.value)}
            </span>
          </p>
        ))}
        {!hasAnyField && (
          <span className="text-xs text-slate-500">No contact information</span>
        )}
      </div>
    </div>
  );
}
